import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  Lightbulb,
  FileText,
  MessageSquare,
  LogOut,
  ChevronRight,
  Settings,
  Shield,
  DollarSign,
  Wallet,
  TrendingUp,
  Building2
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import logo from "@/assets/gc-content-ai-logo.png";

// Agency Management Items - always visible for agencies
const agencyManagementItems = [
  { path: "/agency/dashboard", label: "Agency Dashboard", icon: LayoutDashboard },
  { path: "/agency/workspaces", label: "My Workspaces", icon: Building2 },
  { path: "/agency/ai-usage", label: "AI Usage", icon: DollarSign },
  { path: "/agency/wallet", label: "Agency Wallet", icon: Wallet },
  { path: "/agency/settings", label: "Agency Settings", icon: Settings },
];

// Workspace Features - only visible when workspace is selected
const workspaceFeatureItems = [
  { path: "/dashboard", label: "Content Dashboard", icon: LayoutDashboard },
  { path: "/knowledge", label: "Knowledge Base", icon: BookOpen },
  { path: "/strategy", label: "Content Strategy", icon: Lightbulb },
  { path: "/posts", label: "Post Generator", icon: FileText },
  { path: "/engagement", label: "Engagement", icon: MessageSquare },
  { path: "/trends", label: "Industry Trends", icon: TrendingUp },
  { path: "/viral-content", label: "Viral Content", icon: TrendingUp, badge: "NEW" },
  { path: "/settings", label: "Workspace Settings", icon: Settings },
];

// Regular user navigation
const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/knowledge", label: "Knowledge Base", icon: BookOpen },
  { path: "/strategy", label: "Content Strategy", icon: Lightbulb },
  { path: "/posts", label: "Post Generator", icon: FileText },
  { path: "/engagement", label: "Engagement", icon: MessageSquare },
  { path: "/trends", label: "Industry Trends", icon: TrendingUp },
  { path: "/ai-usage", label: "AI Usage", icon: DollarSign },
  { path: "/wallet", label: "Wallet", icon: Wallet },
  { path: "/settings", label: "Settings", icon: Settings },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const navigate = useNavigate();
  const isCollapsed = state === "collapsed";
  const { isAdmin, isAgency, currentWorkspace } = useAuth();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border/50">
      <SidebarHeader className={cn(
        "border-b border-sidebar-border/50",
        isCollapsed ? "p-4" : "p-6"
      )}>
        <div className={cn("flex items-center", isCollapsed ? "justify-center" : "justify-start")}>
          <img src={logo} alt="GC Content AI" className="h-8 transition-all" />
        </div>
      </SidebarHeader>

      <SidebarContent className={cn(
        "custom-scrollbar",
        isCollapsed ? "px-2 py-4" : "px-3 py-6"
      )}>
        {/* Agency Navigation */}
        {isAgency ? (
          <>
            {/* Agency Management Section */}
            <SidebarGroup>
              {!isCollapsed && (
                <SidebarGroupLabel className="px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
                  Agency Management
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className={cn(isCollapsed ? "space-y-2" : "space-y-1")}>
                  <TooltipProvider delayDuration={0}>
                    {agencyManagementItems.map((item) => (
                      <SidebarMenuItem key={item.path}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild className="group">
                              <NavLink
                                to={item.path}
                                className={({ isActive }) =>
                                  cn(
                                    "flex items-center rounded-xl transition-all duration-200 relative overflow-hidden",
                                    isCollapsed 
                                      ? "justify-center w-10 h-10 p-0" 
                                      : "gap-3 px-3 py-3",
                                    isActive
                                      ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-lg"
                                      : "hover:bg-sidebar-accent/50 text-sidebar-foreground/70 hover:text-sidebar-foreground"
                                  )
                                }
                              >
                                {({ isActive }) => (
                                  <>
                                    {isActive && (
                                      <div className="absolute inset-0 bg-gradient-to-r from-sidebar-primary/10 to-purple-600/10 rounded-xl"></div>
                                    )}
                                    <item.icon className={cn("h-5 w-5 flex-shrink-0 relative z-10", isActive && "text-sidebar-primary")} />
                                    {!isCollapsed && (
                                      <span className="font-medium text-sm relative z-10">{item.label}</span>
                                    )}
                                    {!isCollapsed && isActive && (
                                      <ChevronRight className="h-4 w-4 text-sidebar-primary flex-shrink-0 relative z-10 ml-auto" />
                                    )}
                                  </>
                                )}
                              </NavLink>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          {isCollapsed && (
                            <TooltipContent side="right" className="font-medium">
                              {item.label}
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </SidebarMenuItem>
                    ))}
                  </TooltipProvider>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Workspace Section - only when workspace is selected */}
            {currentWorkspace && (
              <>
                {!isCollapsed && <Separator className="my-4" />}
                <SidebarGroup>
                  {!isCollapsed && (
                    <SidebarGroupLabel className="px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
                      Current Workspace
                    </SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu className={cn(isCollapsed ? "space-y-2" : "space-y-1")}>
                      <TooltipProvider delayDuration={0}>
                        {workspaceFeatureItems.map((item) => (
                          <SidebarMenuItem key={item.path}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <SidebarMenuButton asChild className="group">
                                  <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                      cn(
                                        "flex items-center rounded-xl transition-all duration-200 relative overflow-hidden",
                                        isCollapsed 
                                          ? "justify-center w-10 h-10 p-0" 
                                          : "gap-3 px-3 py-3",
                                        isActive
                                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-lg"
                                          : "hover:bg-sidebar-accent/50 text-sidebar-foreground/70 hover:text-sidebar-foreground"
                                      )
                                    }
                                  >
                                    {({ isActive }) => (
                                      <>
                                        {isActive && (
                                          <div className="absolute inset-0 bg-gradient-to-r from-sidebar-primary/10 to-purple-600/10 rounded-xl"></div>
                                        )}
                                        <item.icon className={cn("h-5 w-5 flex-shrink-0 relative z-10", isActive && "text-sidebar-primary")} />
                                        {!isCollapsed && (
                                          <span className="font-medium text-sm relative z-10">{item.label}</span>
                                        )}
                                        {!isCollapsed && isActive && (
                                          <ChevronRight className="h-4 w-4 text-sidebar-primary flex-shrink-0 relative z-10 ml-auto" />
                                        )}
                                      </>
                                    )}
                                  </NavLink>
                                </SidebarMenuButton>
                              </TooltipTrigger>
                              {isCollapsed && (
                                <TooltipContent side="right" className="font-medium">
                                  {item.label}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </SidebarMenuItem>
                        ))}
                      </TooltipProvider>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </>
            )}
          </>
        ) : (
          /* Regular User Navigation */
          <SidebarGroup>
            {!isCollapsed && (
              <SidebarGroupLabel className="px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
                Navigation
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className={cn(isCollapsed ? "space-y-2" : "space-y-1")}>
                <TooltipProvider delayDuration={0}>
                  {navItems.map((item) => (
                    <SidebarMenuItem key={item.path}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild className="group">
                            <NavLink
                              to={item.path}
                              className={({ isActive }) =>
                                cn(
                                  "flex items-center rounded-xl transition-all duration-200 relative overflow-hidden",
                                  isCollapsed 
                                    ? "justify-center w-10 h-10 p-0" 
                                    : "gap-3 px-3 py-3",
                                  isActive
                                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-lg"
                                    : "hover:bg-sidebar-accent/50 text-sidebar-foreground/70 hover:text-sidebar-foreground"
                                )
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  {isActive && (
                                    <div className="absolute inset-0 bg-gradient-to-r from-sidebar-primary/10 to-purple-600/10 rounded-xl"></div>
                                  )}
                                  <item.icon className={cn("h-5 w-5 flex-shrink-0 relative z-10", isActive && "text-sidebar-primary")} />
                                  {!isCollapsed && (
                                    <span className="font-medium text-sm relative z-10">{item.label}</span>
                                  )}
                                  {!isCollapsed && isActive && (
                                    <ChevronRight className="h-4 w-4 text-sidebar-primary flex-shrink-0 relative z-10 ml-auto" />
                                  )}
                                </>
                              )}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right" className="font-medium">
                            {item.label}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  ))}
                </TooltipProvider>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}


        {isAdmin && (
          <>
            {!isCollapsed && <Separator className="my-4" />}
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className={cn(isCollapsed ? "space-y-2" : "space-y-1")}>
                  <TooltipProvider delayDuration={0}>
                    <SidebarMenuItem>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild className="group">
                            <NavLink
                              to="/admin/dashboard"
                              className={cn(
                                "flex items-center rounded-xl transition-all duration-200 relative overflow-hidden",
                                isCollapsed 
                                  ? "justify-center w-10 h-10 p-0" 
                                  : "gap-3 px-3 py-3",
                                "hover:bg-sidebar-accent/50 text-sidebar-foreground/70 hover:text-sidebar-foreground"
                              )}
                            >
                              <Shield className="h-5 w-5 flex-shrink-0" />
                              {!isCollapsed && (
                                <span className="font-medium text-sm">Admin Panel</span>
                              )}
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right" className="font-medium">
                            Admin Panel
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </SidebarMenuItem>
                  </TooltipProvider>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter className={cn(
        "border-t border-sidebar-border/50 mt-auto",
        isCollapsed ? "p-2" : "p-4"
      )}>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                onClick={handleSignOut}
                className={cn(
                  "hover:bg-destructive/10 hover:text-destructive rounded-xl transition-all",
                  isCollapsed 
                    ? "w-10 h-10 p-0 justify-center" 
                    : "w-full justify-start px-3"
                )}
              >
                <LogOut className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
                {!isCollapsed && <span className="font-medium">Sign Out</span>}
              </Button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right" className="font-medium">
                Sign Out
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </SidebarFooter>
    </Sidebar>
  );
}
